import React, { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import FixedRetargetedAvatar from './FixedRetargetedAvatar';

interface AvatarWithVoiceStateProps {
  avatarUrl: string;
  position: [number, number, number];
  cameraMode: 'normal' | 'zoom' | 'birdseye';
  userName: string;
  onGroupRef?: (ref: THREE.Group | null) => void;
  disableCameraControl?: boolean;
  firstPerson?: boolean;
  onLockChange?: (locked: boolean) => void;
}

const AvatarWithVoiceState = React.memo<AvatarWithVoiceStateProps>(({
  avatarUrl,
  position,
  cameraMode,
  userName,
  onGroupRef,
  disableCameraControl = false,
  firstPerson = false,
  onLockChange,
}) => {
  const { camera } = useThree();
  const avatarRef = useRef<THREE.Group>(null);
  const innerAvatarRef = useRef<THREE.Group | null>(null);
  const savedCameraState = useRef({
    position: new THREE.Vector3(),
    rotation: new THREE.Euler()
  });

  // Save camera state when entering zoom/birdseye, restore on normal
  useEffect(() => {
    if (!avatarRef.current) return;
    if (cameraMode === 'normal' && savedCameraState.current.position.length() > 0) {
      camera.position.copy(savedCameraState.current.position);
      camera.rotation.copy(savedCameraState.current.rotation);
    } else {
      savedCameraState.current.position.copy(camera.position);
      savedCameraState.current.rotation.copy(camera.rotation);
    }
  }, [cameraMode, camera]);

  useFrame(() => {
    // Skip desktop camera follow in VR (XROrigin handles camera)
    if ((window as any).__vrOptimizedMode) return;
    if (!innerAvatarRef.current) return;

    const pos = innerAvatarRef.current.position;
    const rot = innerAvatarRef.current.rotation.y;

    if (cameraMode === 'birdseye') {
      const lookDir = new THREE.Vector3(Math.sin(rot), 0, Math.cos(rot));
      const offset = 0.3;
      camera.position.set(
        pos.x - lookDir.x * offset,
        pos.y + 1.7,
        pos.z - lookDir.z * offset
      );
      camera.lookAt(
        pos.x - lookDir.x * 10,
        pos.y + 1.7,
        pos.z - lookDir.z * 10
      );
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = 60;
        camera.updateProjectionMatrix();
      }
    } else if (camera instanceof THREE.PerspectiveCamera && camera.fov !== 75) {
      camera.fov = 75;
      camera.updateProjectionMatrix();
    }
  });

  return (
    <group ref={avatarRef}>
      <FixedRetargetedAvatar
        avatarUrl={avatarUrl}
        position={position}
        disableCameraControl={disableCameraControl || cameraMode === 'birdseye'}
        cameraDistanceOverride={cameraMode === 'zoom' ? 3 : undefined}
        firstPerson={firstPerson}
        onLockChange={onLockChange}
        onGroupRef={(ref: THREE.Group | null) => {
          if (ref) innerAvatarRef.current = ref;
          if (onGroupRef) onGroupRef(ref);
        }}
        userName={userName}
        showNameTag={cameraMode !== 'birdseye'}
      />
    </group>
  );
});

export default AvatarWithVoiceState;
